import { useDispatch, useSelector } from "react-redux";
import { ConfigStepId, ConfigStepType, SignatureDocument, SignatureStatus } from "../../utils/configuration";
import { AppDispatch, RootState } from "../../configureStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useIsMobile from "../../utils/useIsMobile";
import { BorrowerGuide, checkSignatureCompleted, getService, SigSignSignature } from "../../redux/reducer/applicant";
import { Skeleton, Spin } from "antd";
import { AxiosResponse } from "axios";
import { SUCCESSFUL_MESSAGE } from "../../redux/reducer/status";
import BasicInfoForm from "./BasicInfoForm";
import ErrorPage from "./ErrorPage";

export interface RequestForms {
    formName: string;
    taxYears: number;
}

export type AuthorizationStepType = 'consent' | 'tia' | 'liveness' | 'approval';

export interface TranscriptParams {
    overrideTenantId?: string;
    useTenant: boolean;
    authorization: {
        order?: AuthorizationStepType[];
        consent: {
            enabled: boolean;
            document?: SignatureDocument;
        };
        tia: {
            enabled: boolean;
            document?: SignatureDocument;
        };
        liveness?: {
            enabled: boolean;
            beforeSignature?: boolean;
            actionNeeded?: boolean;
            displayText?: string;
        }
        approval: {
            enabled: boolean;
        };
    };
    forms: RequestForms[];
}

const isTEST = false;

const Transcript = ({
}: TranscriptParams) => {
    const isMobile = useIsMobile();
    const [iframeHeight, setIframeHeight] = useState<number | null>(null);
    const steps = useSelector<RootState, ConfigStepType[]>(({ configuration: { steps } }) => steps);
    const transcriptInfo = useSelector<RootState, SigSignSignature[]>(({ applicant }) => (applicant.transcript ? (Array.isArray(applicant.transcript.info) ? applicant.transcript.info : applicant.transcript.info.signatures) ?? [] : []));
    const guideInfo = useSelector<RootState, BorrowerGuide | undefined>(({ applicant }) => (!applicant.transcript || Array.isArray(applicant.transcript.info)) ? undefined : applicant.transcript.info.guide);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['applicant/GET_SERVICE_INFO']);
    const hasError = useMemo(() => transcriptInfo.some(({ status }) => status === 'Declined'), [transcriptInfo]);
    const signaturesDone = useMemo(() => transcriptInfo.every(({ completed }) => completed), [transcriptInfo]);
    const needsInfo = useSelector<RootState, boolean>(({ applicant }) => Object.values(applicant.applicant?.missingInfo ?? {}).some(Boolean))

    const active = useMemo(() => (isTEST && guideInfo?.url) ? undefined : transcriptInfo.find(({ completed }) => !completed), [transcriptInfo]);

    const dispatch = useDispatch<AppDispatch>();
    const getLink = useCallback(() => {
        const service = steps.find(({ id }) => id === ConfigStepId.transcript);
        if (service) {
            dispatch(getService({
                input: { type: ConfigStepId.transcript },
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            }));
        }
    }, [steps]);

    const hasCalled = useRef(false);

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;

        getLink();
    }, []);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                if (active && !hasError) {
                    dispatch(checkSignatureCompleted({
                        input: { transactionId: active.transactionId },
                        onSuccess: (res: AxiosResponse<{ completed: boolean; transactionId?: string; status: SignatureStatus }>) => {
                            if (res.data.completed) {
                                dispatch({ type: SUCCESSFUL_MESSAGE, message: 'We have received your completed document!' });
                            }
                        },
                    }));
                }
            } catch (err) {
                console.error(err);
            }
        }, 3000); // Poll every 3 seconds

        return () => clearInterval(interval);
    }, [active, hasError]);

    useEffect(() => {
        const handleEvent = (event: MessageEvent) => {
            if (event.origin) {
                switch (event.data.event) {
                    case 'resize':
                    case 'iframeHeight':
                    case 'contentHeight':
                        // Handle height update from iframe
                        if (event.data.height && typeof event.data.height === 'number') {
                            setIframeHeight(event.data.height);
                        }
                        break;
                    default:
                        console.warn('Unrecognized event', event.data);
                }
            }
        };

        window.addEventListener("message", handleEvent);

        return () => {
            window.removeEventListener("message", handleEvent);
        }
    }, [dispatch]);

    return (transcriptInfo.length > 0 && signaturesDone && !guideInfo?.completed && needsInfo) ? (
        <BasicInfoForm stepId={ConfigStepId.transcript} />
    ) : !signaturesDone ? (
        <Skeleton loading={loading || !active} active>
            {hasError ? (
                <ErrorPage
                    title="You declined signature consent"
                    subTitle="We cannot proceed without your electronic signature consent"
                />
            ) : (
                <iframe
                    allow="camera *"
                    src={active?.embedUrl}
                    className="signature_frame"
                    style={{
                        height: iframeHeight ? `${iframeHeight}px` : (isMobile ? '100vh' : '80vh'),
                        minHeight: '1200px',
                        width: '100%',
                        border: 'none',
                        overflow: 'hidden'
                    }}
                />
            )
            }
        </Skeleton >
    ) : guideInfo?.url ? (
        <iframe
            id="guide"
            src={`${guideInfo?.url}&location=halcyondirect`}
            className="signature_frame"
            style={{
                height: iframeHeight ? `${iframeHeight}px` : (isMobile ? '100vh' : '80vh'),
                minHeight: '600px',
                width: '100%',
                border: 'none',
                overflow: 'hidden'
            }}
        />
    ) : <Spin fullscreen />;
};

export default Transcript;
